import { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

/** What the cook wants instead, and why they didn't want this.
 *
 * The dialog is not a confirmation. Swapping is still one-way -- the dinner
 * is marked rejected and every later swap on this week skips it -- but asking
 * "are you sure" bought nothing, because the answer was always yes. What the
 * app was missing is the half of the decision only the cook knows: the reason.
 *
 * That reason is the whole point. The replacement used to come off the top of
 * the score, which cannot tell that the objection was "too heavy two nights
 * running" and is as likely to answer it with another cream sauce as not. It
 * now goes to the model along with the dinners being kept, so the stand-in is
 * chosen against the week as it actually stands.
 *
 * Optional, though. A cook who just wants something different should not have
 * to write an essay to get it, so "Swap it out" never disables.
 *
 * The dialog stays up until the swap lands. Choosing the stand-in is a model
 * call and takes seconds, and closing on the click meant the screen sat there
 * unchanged with nothing to say it was working -- long enough to read as
 * "that did nothing" and press it again. `onSwap` is handed the closer so the
 * dialog goes away when the new dinner is actually on the week, and a refusal
 * is shown here rather than behind a dialog that has already gone.
 */
export type SwapChoice = { reason: string; neverAgain: boolean };

export default function SwapButton({
  recipeTitle,
  pending,
  error,
  onSwap,
}: {
  recipeTitle: string;
  pending: boolean;
  /** Shown inside the dialog, because that is where it was asked for. */
  error?: string;
  onSwap: (choice: SwapChoice, done: () => void) => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [neverAgain, setNeverAgain] = useState(false);

  // Backing out clears the answers. A half-typed reason surviving until the
  // next dinner is swapped would attach it to the wrong recipe.
  const close = () => {
    setAsking(false);
    setReason('');
    setNeverAgain(false);
  };

  return (
    <>
      <button
        className="btn btn--small"
        disabled={pending}
        onClick={() => setAsking(true)}
      >
        Swap
      </button>

      {asking && (
        <ConfirmDialog
          titleId="confirm-swap-title"
          title={`Swap out ${recipeTitle}?`}
          /* Not dismissable mid-swap: Escape or the backdrop would hide a
             request that is still running and land its answer on a screen
             with no sign of where it came from. */
          onCancel={pending ? () => {} : close}
          actions={
            <>
              <button
                className="btn btn--primary"
                disabled={pending}
                onClick={() =>
                  onSwap({ reason: reason.trim(), neverAgain }, close)
                }
              >
                {pending ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Finding something else…
                  </>
                ) : (
                  'Swap it out'
                )}
              </button>
              <button className="link-button" disabled={pending} onClick={close}>
                Keep it
              </button>
            </>
          }
        >
          <label className="field">
            <span>Why didn’t you like this?</span>
            <textarea
              rows={3}
              autoFocus
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Optional — it helps pick something better."
            />
          </label>

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={neverAgain}
              onChange={(event) => setNeverAgain(event.target.checked)}
            />
            <span>Don’t suggest {recipeTitle} again</span>
          </label>

          <p className="muted small">
            The replacement is chosen to sit beside the dinners you’re keeping.
            {recipeTitle} won’t come back on this week’s plan either way, and
            your shopping list changes to match.
          </p>

          {/* Kept in the dialog rather than behind it: a refusal is an answer
              to the question still on screen. */}
          {error && <p className="error">{error}</p>}
        </ConfirmDialog>
      )}
    </>
  );
}
